import { Geometry, Vector3, VertexBuffer } from '@babylonjs/core';

import { PointAttributeName } from '../point-attributes';

import { OctreeGeometry, BoundingBox, Sphere } from './OctreeGeometry';
import { OctreeGeometryNode } from './OctreeGeometryNode';
import {
  PointAttribute,
  PointAttributes,
  PointAttributeTypes,
} from './PointAttributes';
import { WorkerPool, WorkerType } from './WorkerPool';

import type { XhrRequest } from './types';
import type { GetAssetUrlFunctionType } from '../types';

export class NodeLoader {
  attributes?: PointAttributes;

  scale?: [number, number, number];

  offset?: [number, number, number];

  performanceMetrics?: {
    totalTime: number;
    count: number;
    minTime: number;
    maxTime: number;
    executionTimes: number[];
  };

  private enablePerformanceLogging = false; // Default to false

  // eslint-disable-next-line no-use-before-define
  constructor(
    public url: string,
    public getUrl: GetAssetUrlFunctionType,
    public workerPool: WorkerPool,
    public metadata: Metadata, // eslint-disable-line no-use-before-define
  ) {}

  async load(node: OctreeGeometryNode) {
    if (node.loaded || node.loading) {
      return;
    }

    node.loading = true;
    // TODO: Need to put the numNodesLoading to the pco
    node.octreeGeometry.numNodesLoading++;

    try {
      if (node.nodeType === 2) {
        // TODO: Investigate
        // console.log('loadHierarchy', node);
        await this.loadHierarchy(node);
      }

      const { byteOffset, byteSize } = node;

      if (byteOffset === undefined || byteSize === undefined) {
        throw new Error('byteOffset and byteSize are required');
      }

      const startTime = performance.now();

      // let urlOctree = this.url.replace('/metadata.json', '/octree.bin');
      const urlOctree = await this.getUrl('octree.bin', true);
      const first = byteOffset;
      const last = byteOffset + byteSize - BigInt(1);

      const timeToGetUrl = performance.now() - startTime;

      let buffer: ArrayBuffer;
      let retryCount = 0;
      const maxRetries = 1;
      let currentUrl = urlOctree;

      const startTime2 = performance.now();
      if (byteSize === BigInt(0)) {
        buffer = new ArrayBuffer(0);
      } else {
        while (retryCount < maxRetries) {
          try {
            const response = await fetch(currentUrl, {
              headers: {
                Range: `bytes=${first}-${last}`,
              },
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            buffer = await response.arrayBuffer();
            break;
          } catch (error) {
            retryCount++;
            if (retryCount > maxRetries) {
              throw error;
            }

            // Get a fresh URL without using cache
            currentUrl = await this.getUrl('octree.bin', false);
          }
        }
      }
      const timeToGetBuffer = performance.now() - startTime2;

      const endTime = performance.now();
      const executionTime = endTime - startTime;
      const executionTimeInt = Math.floor(executionTime); // Truncate decimal places

      // Performance metrics logging
      if (this.enablePerformanceLogging) {
        if (!this.performanceMetrics) {
          this.performanceMetrics = {
            totalTime: 0,
            count: 0,
            minTime: Infinity,
            maxTime: 0,
            executionTimes: [],
          };
        }
        this.performanceMetrics.totalTime += executionTime;
        this.performanceMetrics.count++;
        this.performanceMetrics.minTime = Math.min(
          this.performanceMetrics.minTime,
          executionTime,
        );
        this.performanceMetrics.maxTime = Math.max(
          this.performanceMetrics.maxTime,
          executionTime,
        );
        this.performanceMetrics.executionTimes.push(executionTimeInt); // Store truncated value

        const avgTime =
          this.performanceMetrics.totalTime / this.performanceMetrics.count;
        const sortedTimes = [...this.performanceMetrics.executionTimes].sort(
          (a, b) => a - b,
        );
        const medianTime =
          this.performanceMetrics.count % 2 === 0
            ? (sortedTimes[this.performanceMetrics.count / 2 - 1] +
                sortedTimes[this.performanceMetrics.count / 2]) /
              2
            : sortedTimes[Math.floor(this.performanceMetrics.count / 2)];

        console.log(`Performance Metrics for octree loading:
          Node: ${node.name}
          Average: ${avgTime.toFixed(2)}ms
          Median: ${medianTime}ms
          Min: ${this.performanceMetrics.minTime.toFixed(2)}ms
          Max: ${this.performanceMetrics.maxTime.toFixed(2)}ms
          Count: ${this.performanceMetrics.count}
          This time: ${executionTime.toFixed(2)}ms
          Time to get url: ${timeToGetUrl.toFixed(2)}ms
          Time to get buffer: ${timeToGetBuffer.toFixed(2)}ms`);
      }

      const workerType =
        this.metadata.encoding === 'BROTLI'
          ? WorkerType.DECODER_WORKER_BROTLI
          : WorkerType.DECODER_WORKER;
      const worker = this.workerPool.getWorker(workerType);

      worker.onmessage = (e) => {
        const data = e.data;
        const buffers = data.attributeBuffers;

        this.workerPool.returnWorker(workerType, worker);

        const geometry = new Geometry('geo');

        for (const property in buffers) {
          const buffer = buffers[property].buffer;

          if (property === 'position') {
            const positionArray = new Float32Array(buffer);
            geometry.setVerticesData(VertexBuffer.PositionKind, positionArray);
          } else if (property === 'rgba') {
            const colorsU8 = new Uint8Array(buffer);
            const colors = new Float32Array(colorsU8.length);

            for (let i = 0; i < colorsU8.length; i++) {
              colors[i] = colorsU8[i] / 255.0;
            }

            geometry.setVerticesData(VertexBuffer.ColorKind, colors);
          } else if (property === 'NORMAL') {
            geometry.setVerticesData(
              VertexBuffer.NormalKind,
              new Float32Array(buffer),
            );
          } else if (property === 'INDICES') {
            geometry.setIndices(new Uint32Array(buffer));
          }

          if (this.isAttribute(property, PointAttributeName.INTENSITY)) {
            geometry.setVerticesData(
              'intensity',
              new Float32Array(buffer),
              false,
            );
          } else if (
            this.isAttribute(property, PointAttributeName.CLASSIFICATION)
          ) {
            const uint8Array = new Uint8Array(buffer);
            const classifications = new Float32Array(uint8Array.length);

            for (let i = 0; i < uint8Array.length; i++) {
              classifications[i] = uint8Array[i];
            }
            geometry.setVerticesData('classification', classifications, false);
          }
        }

        node.density = data.density;
        node.geometry = geometry;
        node.loaded = true;
        node.loading = false;
        node.octreeGeometry.numNodesLoading--;
      };

      const pointAttributes = node.octreeGeometry.pointAttributes;
      const scale = node.octreeGeometry.scale;

      const box = node.boundingBox;
      const min = {
        x: node.octreeGeometry.offset.x,
        y: node.octreeGeometry.offset.y,
        z: node.octreeGeometry.offset.z,
      };

      min.x += box.min.x;
      min.y += box.min.y;
      min.z += box.min.z;

      const size = {
        x: box.max.x - box.min.x,
        y: box.max.y - box.min.y,
        z: box.max.z - box.min.z,
      };

      const max = {
        x: min.x + size.x,
        y: min.y + size.y,
        z: min.z + size.z,
      };

      const numPoints = node.numPoints;

      const offset = node.octreeGeometry.loader.offset;

      const message = {
        name: node.name,
        buffer,
        pointAttributes,
        scale,
        min,
        max,
        size,
        offset,
        numPoints,
      };

      worker.postMessage(message, [message.buffer]);
    } catch (e) {
      node.loaded = false;
      node.loading = false;
      node.octreeGeometry.numNodesLoading--;
    }
  }

  private isAttribute(property: string, name: PointAttributeName): boolean {
    return parseInt(property, 10) === name;
  }

  parseHierarchy(node: OctreeGeometryNode, buffer: ArrayBuffer) {
    const view = new DataView(buffer);

    const bytesPerNode = 22;
    const numNodes = buffer.byteLength / bytesPerNode;

    const octree = node.octreeGeometry;
    // let nodes = [node];
    const nodes: OctreeGeometryNode[] = new Array(numNodes);
    nodes[0] = node;
    let nodePos = 1;

    for (let i = 0; i < numNodes; i++) {
      const current = nodes[i];

      const type = view.getUint8(i * bytesPerNode + 0);
      const childMask = view.getUint8(i * bytesPerNode + 1);
      const numPoints = view.getUint32(i * bytesPerNode + 2, true);
      const byteOffset = view.getBigInt64(i * bytesPerNode + 6, true);
      const byteSize = view.getBigInt64(i * bytesPerNode + 14, true);

      // if(byteSize === 0n){
      // 	// debugger;
      // }

      if (current.nodeType === 2) {
        // replace proxy with real node
        current.byteOffset = byteOffset;
        current.byteSize = byteSize;
        current.numPoints = numPoints;
      } else if (type === 2) {
        // load proxy
        current.hierarchyByteOffset = byteOffset;
        current.hierarchyByteSize = byteSize;
        current.numPoints = numPoints;
      } else {
        // load real node
        current.byteOffset = byteOffset;
        current.byteSize = byteSize;
        current.numPoints = numPoints;
      }

      current.nodeType = type;

      if (current.nodeType === 2) {
        continue;
      }

      for (let childIndex = 0; childIndex < 8; childIndex++) {
        const childExists = ((1 << childIndex) & childMask) !== 0;

        if (!childExists) {
          continue;
        }

        const childName = current.name + childIndex;

        const childAABB = createChildAABB(current.boundingBox, childIndex);
        const child = new OctreeGeometryNode(childName, octree, childAABB);
        child.name = childName;
        child.spacing = current.spacing / 2;
        child.level = current.level + 1;

        (current.children as any)[childIndex] = child;
        child.parent = current;

        // nodes.push(child);
        nodes[nodePos] = child;
        nodePos++;
      }

      // if((i % 500) === 0){
      // 	yield;
      // }
    }

    // if(duration > 20){
    // 	let msg = `duration: ${duration}ms, numNodes: ${numNodes}`;
    // 	console.log(msg);
    // }
  }

  async loadHierarchy(node: OctreeGeometryNode) {
    const { hierarchyByteOffset, hierarchyByteSize } = node;

    if (hierarchyByteOffset === undefined || hierarchyByteSize === undefined) {
      throw new Error(
        `hierarchyByteOffset and hierarchyByteSize are undefined for node ${node.name}`,
      );
    }

    // let hierarchyPath = this.url.replace('/metadata.json', '/hierarchy.bin');
    const hierarchyPath = await this.getUrl('hierarchy.bin');

    const first = hierarchyByteOffset;
    const last = first + hierarchyByteSize - BigInt(1);

    const response = await fetch(hierarchyPath, {
      headers: {
        // 'content-type': 'multipart/byteranges',
        Range: `bytes=${first}-${last}`,
      },
    });

    const buffer = await response.arrayBuffer();

    this.parseHierarchy(node, buffer);
  }
}

function createChildAABB(aabb: BoundingBox, index: number) {
  const min = aabb.min.clone();
  const max = aabb.max.clone();
  const size = new Vector3(max.x - min.x, max.y - min.y, max.z - min.z);

  if ((index & 0b0001) > 0) {
    min.z += size.z / 2;
  } else {
    max.z -= size.z / 2;
  }

  if ((index & 0b0010) > 0) {
    min.y += size.y / 2;
  } else {
    max.y -= size.y / 2;
  }

  if ((index & 0b0100) > 0) {
    min.x += size.x / 2;
  } else {
    max.x -= size.x / 2;
  }

  return new BoundingBox(min, max);
}

const typenameTypeattributeMap = {
  double: PointAttributeTypes.DATA_TYPE_DOUBLE,
  float: PointAttributeTypes.DATA_TYPE_FLOAT,
  int8: PointAttributeTypes.DATA_TYPE_INT8,
  uint8: PointAttributeTypes.DATA_TYPE_UINT8,
  int16: PointAttributeTypes.DATA_TYPE_INT16,
  uint16: PointAttributeTypes.DATA_TYPE_UINT16,
  int32: PointAttributeTypes.DATA_TYPE_INT32,
  uint32: PointAttributeTypes.DATA_TYPE_UINT32,
  int64: PointAttributeTypes.DATA_TYPE_INT64,
  uint64: PointAttributeTypes.DATA_TYPE_UINT64,
};

type AttributeType = keyof typeof typenameTypeattributeMap;

export interface Attribute {
  name: string;
  description: string;
  size: number;
  numElements: number;
  type: AttributeType;
  min: number[];
  max: number[];
}

export interface Metadata {
  version: string;
  name: string;
  description: string;
  points: number;
  projection: string;
  hierarchy: {
    firstChunkSize: number;
    stepSize: number;
    depth: number;
  };
  offset: [number, number, number];
  scale: [number, number, number];
  spacing: number;
  boundingBox: {
    min: [number, number, number];
    max: [number, number, number];
  };
  encoding: string;
  attributes: Attribute[];
}

export class OctreeLoader {
  workerPool: WorkerPool = new WorkerPool();

  constructor() {}

  static parseAttributes(jsonAttributes: Attribute[]) {
    const attributes = new PointAttributes();

    // Replacements object for string to string
    const replacements: { [key: string]: string } = { rgb: 'rgba' };

    for (const jsonAttribute of jsonAttributes) {
      const { name, numElements, min, max } = jsonAttribute;

      const type = typenameTypeattributeMap[jsonAttribute.type]; // Fix the typing, currently jsonAttribute has type 'never'

      const potreeAttributeName = replacements[name]
        ? replacements[name]
        : name;

      const attribute = new PointAttribute(
        potreeAttributeName,
        type,
        numElements,
      );

      if (numElements === 1) {
        attribute.range = [min[0], max[0]];
      } else {
        attribute.range = [min, max];
      }

      if (name === 'gps-time') {
        // HACK: Guard against bad gpsTime range in metadata, see potree/potree#909
        if (
          typeof attribute.range[0] === 'number' &&
          attribute.range[0] === attribute.range[1]
        ) {
          attribute.range[1] += 1;
        }
      }

      attribute.initialRange = attribute.range;

      attributes.add(attribute);
    }

    {
      // check if it has normals
      const hasNormals =
        attributes.attributes.find((a) => {
          return a.name === 'NormalX';
        }) !== undefined &&
        attributes.attributes.find((a) => {
          return a.name === 'NormalY';
        }) !== undefined &&
        attributes.attributes.find((a) => {
          return a.name === 'NormalZ';
        }) !== undefined;

      if (hasNormals) {
        const vector = {
          name: 'NORMAL',
          attributes: ['NormalX', 'NormalY', 'NormalZ'],
        };
        attributes.addVector(vector);
      }
    }

    return attributes;
  }

  async load(
    url: string,
    getUrl: GetAssetUrlFunctionType,
    xhrRequest: XhrRequest,
  ) {
    // Previously a static method

    const response = await xhrRequest(url);
    const metadata: Metadata = await response.json();

    const attributes = OctreeLoader.parseAttributes(metadata.attributes);

    const loader = new NodeLoader(url, getUrl, this.workerPool, metadata);
    loader.attributes = attributes;
    loader.scale = metadata.scale;
    loader.offset = metadata.offset;

    const octree = new OctreeGeometry(
      loader,
      new BoundingBox(
        new Vector3(
          metadata.boundingBox.min[0],
          metadata.boundingBox.min[1],
          metadata.boundingBox.min[2],
        ),
        new Vector3(
          metadata.boundingBox.max[0],
          metadata.boundingBox.max[1],
          metadata.boundingBox.max[2],
        ),
      ),
    );
    octree.url = url;
    octree.spacing = metadata.spacing;
    octree.scale = metadata.scale;

    const min = new Vector3(
      metadata.boundingBox.min[0],
      metadata.boundingBox.min[1],
      metadata.boundingBox.min[2],
    );
    const max = new Vector3(
      metadata.boundingBox.max[0],
      metadata.boundingBox.max[1],
      metadata.boundingBox.max[2],
    );
    let boundingBox = new BoundingBox(min, max);

    const offset = min.clone();

    // 新しいバウンディングボックスを作成（オフセットを適用）
    const minOffset = new Vector3(0, 0, 0);
    const maxOffset = new Vector3(max.x - min.x, max.y - min.y, max.z - min.z);
    boundingBox = new BoundingBox(minOffset, maxOffset);

    octree.projection = metadata.projection;
    octree.boundingBox = boundingBox;
    octree.tightBoundingBox = boundingBox.clone();
    octree.boundingSphere = boundingBox.getBoundingSphere(new Sphere());
    octree.tightBoundingSphere = boundingBox.getBoundingSphere(new Sphere());
    octree.offset = offset;
    octree.pointAttributes = OctreeLoader.parseAttributes(metadata.attributes);

    const root = new OctreeGeometryNode('r', octree, boundingBox);
    root.level = 0;
    root.nodeType = 2;
    root.hierarchyByteOffset = BigInt(0);
    root.hierarchyByteSize = BigInt(metadata.hierarchy.firstChunkSize);
    root.spacing = octree.spacing;
    root.byteOffset = BigInt(0); // Originally 0

    octree.root = root;

    loader.load(root);

    const result = { geometry: octree };

    return result;
  }
}
